import { pick, set } from "lodash-es";
import includes from "lodash-es/includes";
import intersection from "lodash-es/intersection";
import isArray from "lodash-es/isArray";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import pull from "lodash-es/pull";
import sortBy from "lodash-es/sortBy";
import unset from "lodash-es/unset";
import { action, autorun, makeAutoObservable, runInAction } from "mobx";
import * as z from "zod";
import { ACCEPT_FALLBACK_HEADER, apiClient } from "../../common/apiClient";
import { STATICDATA_HOST } from "../../common/environment";
import { reportError } from "../../common/errorReporting";
import { BackendIssueError, ensureError } from "../../common/errors";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import { FEATURE_NAME } from "../../common/types/featureTypes";
import type { ORDERTYPE } from "../../common/types/productOrderTypes";
import type {
   AssortmentEntry,
   Badge,
   DerivedProduct,
   Filters,
   LegalFilterKey,
   Product,
   ProductResolutionType,
   RawProduct,
   ShortcutFilter
} from "../../common/types/productTypes";
import type { AlgoliaProductHit } from "../../common/types/searchTypes";
import { accumulate } from "../../common/utils";
import { type AsyncData, initializeWithDefaultData, setAsDataAvailable, setAsLoading } from "../../common/utils/asyncDataUtils";
import { formatM3Date } from "../../common/utils/dateUtils";
import { getAssortmentFromStorage, setAssortmentInStorage } from "../../common/utils/storage/storageUtils";
import { ZAssortmentEntries, ZProductAvailabilities, ZProductsCategoriesMetadata } from "../../common/zodSchemas/product";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import availabilityStore from "../availabilityStore";
import cartStore from "../cart/cartStore";
import categoryStore from "../categories/categoryStore";
import deliveryDatesStore from "../deliveryDates/deliveryDatesStore";
import featuresStore from "../features/featuresStore";
import promotionStore from "../promotionStore";
import uiStore from "../uiStore";
import {
   arrayOrUndefined,
   buildManualShortcuts,
   checkShortcutFilter,
   fixDanglingUnits,
   mapBadges,
   mapLabels,
   matchesFilter
} from "./productUtils";

type AssortmentCacheMetadata = { cachedAt: string } | undefined;

type AssortmentCacheEntry = {
   entries: AssortmentEntry[];
   cacheMetadata: AssortmentCacheMetadata;
};

type AssortmentCache = {
   [customerNumber: string]: { [orderType in ORDERTYPE]: { [date: string]: AssortmentCacheEntry } };
};

type AvailabilityMap = { [sku: string]: number };

type ProductStoreType = BaseStoreType & {
   products: Product[];
   viewableProducts: Product[];
   expiredSKUs: string[];
   productsLoaded: boolean;
   productsReady: Promise<void>;
   guestAssortment: string[];
   assortment: string[];
   assortmentCache: AssortmentCache;
   cacheMetadata: AssortmentCacheMetadata;
   showCacheRecoveredAlert: boolean;
   favorites: string[];
   shortcutFilter: ShortcutFilter | "";
   activeFilters: Filters;
   stockLevels: Record<string, AsyncData<number | null>>;
   filterOptions: Partial<Record<LegalFilterKey, string[]>>;

   addFilterValue(field: LegalFilterKey, value: string): void;
   removeFilterValue(field: LegalFilterKey, value: string): void;
   isFilterActive(field: LegalFilterKey, value: string): boolean;
   filterWithActiveFilters(products: Product[]): Product[];
   clearFilter(): void;
   clearFilterAndShortcut(): void;
   clearFilterFields(fields: LegalFilterKey[]): void;
   checkStockForSku(sku: string): Promise<void>;
   checkStockForSkus(skus: string[]): Promise<AvailabilityMap>;
   inAssortment(sku: string): boolean;
   resolveSkus(skus: string[], filter?: ProductResolutionType): Product[];
   resolveSku(sku: string | null | undefined, filter?: ProductResolutionType): Product | undefined;
   findProductsByEAN(ean: string): Product[];
   findProductByUrlKey(urlKey: string): Product | undefined;
   fillStore(): Promise<void>;
   loadProducts(): Promise<void>;
   loadGuestAssortment(): void;
   getUrl(product: Product | AlgoliaProductHit): string;
   loadAssortment(customerNumber: string | null | undefined, date: Date | null | undefined, orderType: ORDERTYPE): Promise<void>;
   fetchAssortment(customerNumber: string | null | undefined, date: Date | null | undefined, orderType: ORDERTYPE): Promise<void>;
   retryAssortmentAfterFallback(): Promise<void>;
   setNewAssortment(assortment: AssortmentEntry[], isGuestAssortment?: boolean): void;
   dismissCacheRecoveredAlert(): void;
};

const enhanceProduct = (product: RawProduct | Product): Product => {
   const derivedProduct: DerivedProduct =
      "badges" in product
         ? (pick(product, [
              "badges",
              "labels",
              "newSkus",
              "inAgreementAssortment",
              "price",
              "unit",
              "priceBeforeDiscount",
              "comparisonPrice",
              "comparisonUnit",
              "vatRate",
              "hasPromotion"
           ]) as DerivedProduct)
         : {
              badges: [] as Badge[],
              labels: [],
              inAgreementAssortment: false,
              price: null,
              unit: product.salesUnit,
              priceBeforeDiscount: null,
              comparisonPrice: null,
              comparisonUnit: null,
              vatRate: null
           };
   if (product.expired) {
      const newSkus = arrayOrUndefined(product.replacementProducts) ?? arrayOrUndefined(product.similarProducts) ?? [];
      if (newSkus.length) {
         derivedProduct.newSkus = newSkus;
      }

      logger.log("products", `Product ${product.sku} is marked as not allowed to purchase in Plytix, new SKUs:`, newSkus);
   }

   const name = fixDanglingUnits(product.name)?.replace(/1\/2/g, "½")?.replace(/1\/4/g, "¼")?.replace(/1\/3/g, "⅓");

   return { ...product, name, ...derivedProduct, badges: mapBadges(product), labels: mapLabels(product) };
};

const productStore: ProductStoreType = makeAutoObservable({
   products: [],
   productsLoaded: false,
   productsReady: Promise.resolve(),
   guestAssortment: [],
   assortment: getAssortmentFromStorage(),
   assortmentCache: {},
   cacheMetadata: undefined,
   showCacheRecoveredAlert: false,
   favorites: [],
   activeFilters: {},
   shortcutFilter: "",
   stockLevels: {},

   filterOptions: {},

   get viewableProducts() {
      return productStore.products.filter((p) => {
         if (productStore.inAssortment(p.sku)) {
            return true;
         }

         // We might want to show it anyway, if it has a replacement SKU that is in assortment
         if (p.expired && p.newSkus) {
            return p.newSkus.some((sku) => productStore.inAssortment(sku));
         }

         return false;
      });
   },

   get expiredSKUs() {
      return productStore.products.filter((p) => p.expired === true).map((p) => p.sku);
   },

   addFilterValue: (field, value) => {
      if (isUndefined(productStore.activeFilters[field])) {
         productStore.activeFilters[field] = [value];
         productStore.activeFilters = { ...productStore.activeFilters };
      } else {
         if (productStore.activeFilters[field].includes(value)) {
            return;
         }
         productStore.activeFilters[field] = [...productStore.activeFilters[field], value];
      }
   },

   removeFilterValue: (field, value) => {
      if (isUndefined(productStore.activeFilters[field])) {
         return;
      }

      pull(productStore.activeFilters[field], value);
      if (productStore.activeFilters[field].length === 0) {
         unset(productStore.activeFilters, field);
      }
      productStore.activeFilters = { ...productStore.activeFilters };
   },

   isFilterActive: (field, value) => {
      if (isUndefined(productStore.activeFilters[field])) {
         return false;
      }
      return includes(productStore.activeFilters[field], value);
   },

   filterWithActiveFilters: (products) => {
      const { shortcutFilter } = productStore;
      const productsInCurrentShortcut = shortcutFilter
         ? products.filter((p) => checkShortcutFilter(categoryStore.manualShortcuts, p, shortcutFilter))
         : products;
      return productsInCurrentShortcut.filter((p) => matchesFilter(p, productStore.activeFilters));
   },
   clearFilterAndShortcut: () => {
      productStore.activeFilters = {};
      productStore.shortcutFilter = "";
   },
   clearFilter: () => {
      productStore.activeFilters = {};
   },
   clearFilterFields: (fields) => {
      for (const field of fields) {
         unset(productStore.activeFilters, field);
      }
      productStore.activeFilters = { ...productStore.activeFilters };
   },
   checkStockForSkus: async (skus: string[]) => {
      if (!skus.length) {
         return {};
      }
      const params = {
         customerNumber: authStore.currentCompany,
         m3OrderType: cartStore.orderType,
         requestedDeliveryDate: formatM3Date(deliveryDatesStore.getCurrentDelivery()?.date),
         products: skus.map((sku) => `${sku}*10`).join("|")
      };
      try {
         const productAvailabilityUrl = resolvePlatformRequestUrl(platformRoutes.productAvailability, { storeId: theme.storeId });
         const resp: z.infer<typeof ZProductAvailabilities> = await apiClient(productAvailabilityUrl, authStore.getSessionToken())
            .query(params)
            .get()
            .json();

         const validation = z.safeParse(ZProductAvailabilities, resp);
         if (validation.success) {
            logger.log("products", "successful validation of availability (check SKUs)", resp);
         } else {
            logger.error("products", "validation failed for availability (check SKUs)", validation, resp);
         }

         return resp.map((item) => ({ [item.productNumber]: item.availableQuantity || 0 })).reduce(accumulate, {});
      } catch (_err) {
         return {};
      }
   },
   checkStockForSku: async (sku) => {
      if (!isUndefined(productStore.stockLevels[sku])) {
         logger.log("products", `Already cached. Not going to do stock check for SKU ${sku}`);
         return;
      }

      const params = {
         customerNumber: authStore.currentCompany,
         m3OrderType: cartStore.orderType,
         requestedDeliveryDate: formatM3Date(deliveryDatesStore.getCurrentDelivery()?.date),
         products: `${sku}*10`
      };

      productStore.stockLevels[sku] = initializeWithDefaultData(null);
      setAsLoading(productStore.stockLevels[sku]);

      try {
         const productAvailabilityUrl = resolvePlatformRequestUrl(platformRoutes.productAvailability, { storeId: theme.storeId });
         const resp: z.infer<typeof ZProductAvailabilities> = await apiClient(productAvailabilityUrl, authStore.getSessionToken())
            .query(params)
            .get()
            .json();

         const validation = z.safeParse(ZProductAvailabilities, resp);
         if (validation.success) {
            logger.log("products", "successful validation of availability (check SKU)", resp);
         } else {
            logger.error("products", "validation failed for availability (check SKU)", validation, resp);
         }

         setAsDataAvailable(productStore.stockLevels[sku], resp[0]?.availableQuantity ?? 15);
      } catch (_err) {
         // Set it to 15 to avoid showing errors on page if the call fails, lets call it an optimistic failure
         setAsDataAvailable(productStore.stockLevels[sku], 15);
      }
   },

   inAssortment: (sku) => {
      return includes(productStore.assortment, sku);
   },

   resolveSkus: (skus, filter: ProductResolutionType = "viewable") => {
      const { products, assortment, viewableProducts, resolveSku } = productStore;
      if (!isArray(skus) || skus.length === 0) {
         return [];
      }
      let filteredSkus: string[] = [];
      if (filter === "allProducts" || filter === "viewable") {
         filteredSkus = skus;
      }
      if (filter === "inAssortment") {
         filteredSkus = intersection(skus, assortment);
      }
      if (filter === "buyable") {
         const resolvedProducts = skus.map((sku) => resolveSku(sku, "buyable")).filter((p) => !!p) as Product[];
         return sortBy(resolvedProducts, (p) => skus.indexOf(p.sku));
      }
      if (filteredSkus.length) {
         const relevantProducts = filter === "viewable" ? viewableProducts : products;
         const resolvedProducts = relevantProducts.filter((p) => filteredSkus.includes(p.sku));
         return sortBy(resolvedProducts, (p) => skus.indexOf(p.sku));
      }
      return [];
   },

   resolveSku: (sku: string, filter: ProductResolutionType = "allProducts") => {
      const { products, assortment, viewableProducts } = productStore;

      if (filter === "allProducts") {
         return products.find((p) => p.sku === sku);
      }
      if (filter === "inAssortment" && assortment.includes(sku)) {
         return products.find((p) => p.sku === sku);
      }
      if (filter === "viewable") {
         return viewableProducts.find((p) => p.sku === sku);
      }
      if (filter === "buyable" && assortment.includes(sku)) {
         const product = products.find((p) => p.sku === sku);
         return !product?.expired && !availabilityStore.isUnavailable(sku) ? product : undefined;
      }
      return undefined;
   },

   findProductsByEAN: (ean) => {
      return productStore.products
         .filter((p) => productStore.inAssortment(p.sku) && p.eans)
         .filter((p) => p.eans?.some((e) => e.code === ean));
   },

   findProductByUrlKey: (urlKey) => {
      return productStore.products.find((p) => p.url_key === urlKey);
   },

   fillStore: async () => {
      const productCategoryMetadataUrl = `${STATICDATA_HOST}/${theme.storeId}/${uiStore.dataVersion}/products-categories-metadata.json`;
      logger.log("products", `Fetching products from ${productCategoryMetadataUrl}`);

      const productDataPromise = apiClient(productCategoryMetadataUrl)
         .get()
         .json(
            action((res: z.infer<typeof ZProductsCategoriesMetadata>) => {
               const validation = z.safeParse(ZProductsCategoriesMetadata, res);
               if (validation.success) {
                  logger.log("products", "successful validation of products/categories/metadata", res);
               } else {
                  logger.error("products", "validation failed for products/categories/metadata", validation, res);
               }
               const { products } = res;
               productStore.products = products.map((p): Product => enhanceProduct(p));
               productStore.filterOptions = res.metadata.options || {};
               productStore.productsLoaded = true;
               logger.log("products", `Products loaded: ${productStore.products.length}`);

               categoryStore.categories = res.categories;
               categoryStore.manualShortcuts = buildManualShortcuts(
                  productStore.products,
                  res.metadata.options?.manualshortcuts || []
               );
               logger.log("categories", `Categories loaded: ${categoryStore.categories.length} root categories`);

               return;
            })
         )
         .catch((err) => {
            logger.error("products", `Error loading products: ${err}`);
            logger.error("categories", `Error loading categories from Plytix: ${err}`);
            throw err;
         });

      const guestAssortmentUrl = `${STATICDATA_HOST}/${theme.storeId}/${uiStore.dataVersion}/guest-assortment.json`;
      logger.log("products", `Fetching guest assortment from ${guestAssortmentUrl}`);

      const guestAssortmentPromise = apiClient(guestAssortmentUrl)
         .get()
         .json(
            action((res: string[]) => {
               if (Array.isArray(res)) {
                  productStore.guestAssortment = res;
                  logger.log("products", `Guest assortment loaded: ${productStore.guestAssortment.length}`);
               } else {
                  throw new Error("guest assortment was not an array");
               }
               return undefined;
            })
         )
         .catch((err) => {
            logger.error("products", `Error loading guest assortment: ${err}`);
            return;
         });

      // If anyone waits for this, they have to wait until both promises are settled. It rejects if the products failed to load.
      return Promise.all([guestAssortmentPromise, productDataPromise]).then(() => {
         return;
      });
   },

   loadProducts: () => {
      productStore.productsReady = productStore
         .fillStore()
         .catch(async (err) => {
            // Reloads on a stale CDN data version (BlobNotFound); throws again otherwise
            await uiStore.handleMissingDataFileError(ensureError(err));
            // The page is reloading, so never settle rather than continue without products
            return new Promise<void>(() => {});
         })
         .then(() => productStore.loadGuestAssortment())
         .catch((err) => {
            reportError(ensureError(err), { context: "Product catalog loading" });
            throw err;
         });
      // Avoids unhandled rejections when no one is logging in; authStore.verifySession handles the failure
      productStore.productsReady.catch(() => {});
      return productStore.productsReady;
   },

   // We fake the object format for each product since we are just resetting old logged in info
   loadGuestAssortment: () => {
      const fakeAssortmentResult: AssortmentEntry[] = productStore.guestAssortment.map((sku) => ({
         itemId: sku,
         inAgreementAssortment: false,
         dpakPrice: null,
         dpakPriceBeforeDiscount: null,
         comparisonPrice: null,
         comparisonPriceUnit: null,
         hasPromotion: false
      }));
      productStore.setNewAssortment(fakeAssortmentResult, true);

      if (theme.searchConfig) {
         void categoryStore.updateCategorySkus();
      }
   },

   getUrl: (product) => {
      return `/produkt/${product.url_key}.html`;
   },

   loadAssortment: async (customerNumber, date, orderType) => {
      const m3Date = formatM3Date(date);
      const cachedEntry = customerNumber ? productStore.assortmentCache[customerNumber]?.[orderType]?.[m3Date] : undefined;
      if (cachedEntry) {
         runInAction(() => {
            productStore.cacheMetadata = cachedEntry.cacheMetadata;
            productStore.setNewAssortment(cachedEntry.entries);
         });
         return;
      }
      return productStore.fetchAssortment(customerNumber, date, orderType);
   },

   fetchAssortment: async (customerNumber, date, orderType) => {
      if (isNil(customerNumber)) {
         return Promise.reject("No customer number given for assortment");
      }
      if (isNil(date)) {
         return Promise.reject("No date specified for assortment");
      }
      if (isNil(orderType)) {
         return Promise.reject("No order type specified for assortment");
      }

      logger.log("products", `Fetching assortment for ${customerNumber} on ${date} for order type ${orderType}`);

      const m3Date = formatM3Date(date);
      let responseCacheMetadata: AssortmentCacheMetadata;
      let assortment: z.infer<typeof ZAssortmentEntries>;

      try {
         const customerAssortmentUrl = resolvePlatformRequestUrl(platformRoutes.customerAssortment, { storeId: theme.storeId });
         assortment = (await apiClient(customerAssortmentUrl, authStore.getSessionToken())
            .headers(ACCEPT_FALLBACK_HEADER)
            .query({
               CONO: theme.m3CompanyNumber,
               CUNO: customerNumber,
               DIVI: theme.m3DivisionNumber,
               UNIT: "BOTH",
               ORTP: orderType,
               DATE: m3Date
            })
            .get()
            .res((response) => {
               const cachedAt = response.headers.get("X-Cache-Timestamp");
               if (cachedAt) {
                  responseCacheMetadata = { cachedAt };
               }
               return response.json();
            })) as z.infer<typeof ZAssortmentEntries>;

         if ("error" in assortment) {
            throw new BackendIssueError("Unable to load assortment, error from backend");
         }
      } catch (err) {
         logger.warn("products", `Unable to fetch assortment - ${err}`);
         throw err;
      }

      const validation = z.safeParse(ZAssortmentEntries, assortment);
      if (validation.success) {
         logger.log("products", "successful validation of assortment", assortment);
      } else {
         logger.error("products", "validation failed for assortment", validation, assortment);
      }

      runInAction(() => {
         const wasOnFallback = productStore.cacheMetadata !== undefined;
         const isOnFallback = responseCacheMetadata !== undefined;
         if (wasOnFallback && !isOnFallback) {
            productStore.showCacheRecoveredAlert = true;
            productStore.assortmentCache = {};
         } else if (isOnFallback) {
            productStore.showCacheRecoveredAlert = false;
         }

         set(productStore.assortmentCache, [customerNumber, orderType, m3Date], {
            entries: assortment,
            cacheMetadata: responseCacheMetadata
         });
         productStore.cacheMetadata = responseCacheMetadata;
         logger.log("products", `Loaded assortment info with ${assortment.length} items`);
         productStore.setNewAssortment(assortment);
      });
   },

   retryAssortmentAfterFallback: async () => {
      if (!authStore.isLoggedIn() || !productStore.cacheMetadata?.cachedAt) {
         return;
      }

      try {
         await productStore.fetchAssortment(
            authStore.currentCompany,
            deliveryDatesStore.getCurrentDelivery()?.date,
            cartStore.orderType
         );
      } catch (err) {
         logger.warn(
            "products",
            "Unable to refresh assortment cache, but session is valid. Will retry next time user becomes active.",
            err
         );
      }
   },

   setNewAssortment: (assortment: AssortmentEntry[], isGuestAssortment = false) => {
      const canBuySingleItems = featuresStore.hasCustomerFeature(FEATURE_NAME.canBuySingleItems);

      const filteredAssortment = isGuestAssortment
         ? assortment
         : assortment.filter((p) => {
              if (isNil(p.dpakPrice)) {
                 if (theme.allowFreeProducts) {
                    p.dpakPrice = 0;
                 } else {
                    return false;
                 }
              }
              const product = productStore.resolveSku(p.itemId, "viewable");
              return !product?.expired;
           });

      productStore.assortment = filteredAssortment.map((p) => p.itemId);

      for (const ap of assortment) {
         const p = productStore.resolveSku(ap.itemId);
         if (!isUndefined(p)) {
            if (p.buyableInBaseUnit && canBuySingleItems) {
               p.price = ap.fpakPrice ?? null;
               p.unit = p.baseUnit;
            } else {
               p.price = ap.dpakPrice;
               p.unit = p.salesUnit;
            }
            p.priceBeforeDiscount = ap.dpakPriceBeforeDiscount;
            p.inAgreementAssortment = ap.inAgreementAssortment;
            p.labels = mapLabels(p);
            p.comparisonPrice = ap.comparisonPrice;
            p.comparisonUnit = ap.comparisonPriceUnit;
            p.vatRate = ap.VAT ?? null;
            p.hasPromotion = promotionStore.hasPromotionDiscount(p.sku);
         }
      }

      categoryStore.hideEmptyCategories();
   },

   dismissCacheRecoveredAlert() {
      productStore.showCacheRecoveredAlert = false;
   },

   clearCompanySpecificData() {
      productStore.cacheMetadata = undefined;
      productStore.showCacheRecoveredAlert = false;
      productStore.assortmentCache = {};
      productStore.loadGuestAssortment();
   }
} satisfies ProductStoreType);

autorun(() => setAssortmentInStorage(productStore.assortment));

autorun(() => {
   logger.log("products", "Assortment changed:", productStore.assortment);
});

export default productStore;
